/* Atlas v3 — "The Cycle", retail edition: the story-first read of the AI
   cycle & supply-chain brain (/api/cycle-panel?series=1).

   Three altitudes:
   1. HERO — flat world dot-map (CycleMap; the signal geographies pulse,
      red where a signal is turning) + the pure-template hero sentence +
      The Read (the weekly guardrailed Sonnet paragraph).
   2. THE SIGNALS — node groups renamed for humans (cycleCopy), each cell a
      plain-English hairline row: name + current direction phrase, spark,
      health arrow, state pill.
   3. TAP A SIGNAL — CycleCellSheet: the dense data (level percentile,
      trend, window, freshness) + "what this means" / "why we track it" /
      source citation. Nothing from the instrument panel is lost — it moved
      one tap deeper.

   Canon: hairline rows (no card stacks), amber = line/label only, mono
   numerals, green/red = direction only, no starfield (Overview-only). */
import { useState, useEffect, useRef } from 'react'
import { TickerCtx, Kicker, Spark, Skel } from './lib'
import { TickerStrip, BottomNav, SearchPill } from './shell'
import { StockDetailProvider, useStockDetail } from '@/v2/lib/StockDetailProvider'
import { useCyclePanel } from '@/v2/hooks/useCyclePanel'
import { useCycleSeries } from '@/v2/hooks/useCycleSeries'
import { useUserPref } from '@/v2/hooks/useUserPref'
import { CycleMap } from './CycleMap'
import { CycleChart } from './CycleChart'
import { NODE_COPY, CELL_COPY } from './cycleCopy'

const Mono = { fontFamily: 'var(--studio-font-mono)', fontVariantNumeric: 'tabular-nums' }
const Sans = { fontFamily: 'var(--studio-font-sans)' }
const Display = { fontFamily: 'var(--studio-font-display)', fontStyle: 'italic' }

/* headline state → label + semantic tone (danger only for the true alarms) */
const STATE_META = {
  'supply-shock': { label: 'SUPPLY SHOCK', tok: '--studio-danger' },
  'demand-turning': { label: 'DEMAND TURNING', tok: '--studio-danger' },
  'early-warning': { label: 'EARLY WARNING', tok: '--studio-accent' },
  divergent: { label: 'DIVERGENT', tok: '--studio-accent' },
  stable: { label: 'STABLE', tok: '--studio-success' },
  'insufficient-data': { label: 'INSUFFICIENT DATA', tok: '--studio-text-subtle' },
}
const SEV = ['QUIET', 'WATCH', 'BREAK']
const SEV_TOK = ['--studio-text-subtle', '--studio-accent', '--studio-danger']

// Demand nodes in read order: leading voters first, then the rest.
const NODE_ORDER = ['credit', 'wfe', 'inference-durability', 'power-deployment', 'chip-demand', 'memory-asp', 'capex-source']

/* bordered mono state pill (the SignalBadge idiom — never a filled surface) */
function Pill({ tok = '--studio-text-subtle', children }) {
  return (
    <span style={{
      ...Mono, fontSize: 8.5, letterSpacing: '0.08em', textTransform: 'uppercase',
      padding: '2px 6px', borderRadius: 'var(--studio-radius-sm)', whiteSpace: 'nowrap',
      color: `hsl(var(${tok}))`, border: `1px solid hsl(var(${tok}) / 0.35)`,
      background: `hsl(var(${tok}) / 0.12)`,
    }}>{children}</span>
  )
}

const fmtLevel = (v) => (v == null ? '—' : `P${Math.round(v * 100)}`)
const monthsAgo = (asOf) => {
  if (!asOf) return null
  const d = Math.round((Date.now() - Date.parse(asOf)) / 86400000)
  return d < 45 ? `${d}D AGO` : `${Math.round(d / 30)}MO AGO`
}
const fmtWeekOf = (iso) => {
  const t = Date.parse(iso)
  if (!Number.isFinite(t)) return null
  return new Date(t).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }).toUpperCase()
}

// Is the recent slope moving TOWARD health? (direction-adjusted)
const improvingOf = (m) => (m.slope == null ? null : (m.weakensWhen === 'rising' ? m.slope < 0 : m.slope > 0))

/* ── chart identity, all derived from the wire (grain/window/isYoY) ── */
const GRAIN_TAG = { daily: 'D', monthly: 'M', quarterly: 'Q' }
const GRAIN_WORD = { daily: 'DAYS', monthly: 'MONTHS', quarterly: 'QUARTERS' }
const MIN_SPARK_POINTS = 8 // fewer → "collecting data", never a fake-flat line
const hasSpark = (m) => (m.series?.length ?? 0) >= MIN_SPARK_POINTS
// Row caption: honest span of the drawn line, e.g. 24D / 24M / 24Q.
const spanTag = (m) => {
  const n = m.series?.length ?? 0
  return n > 1 && GRAIN_TAG[m.grain] ? `${n}${GRAIN_TAG[m.grain]}` : null
}
// Sheet caption: LAST 24 MONTHS · % VS YEAR AGO (or the cell's unit).
const chartCaption = (m, copy) => {
  const n = m.series?.length ?? 0
  const word = GRAIN_WORD[m.grain]
  if (!n || !word) return null
  const metric = m.isYoY ? '% VS YEAR AGO' : copy?.unit ?? null
  return `LAST ${n} ${word}${metric ? ` · ${metric}` : ''}`
}
// Latest point of the drawn series — just the decorated number; the chart
// caption right above carries the unit, so the fact cell can't overflow.
const fmtLatest = (m, copy) => {
  const v = m.series?.[m.series.length - 1]
  if (v == null || !Number.isFinite(v)) return null
  if (m.isYoY) return `${v > 0 ? '+' : ''}${(Math.round(v * 10) / 10)}%`
  const a = Math.abs(v)
  const num = a >= 1000 ? Math.round(v).toLocaleString('en-US') : a >= 100 ? v.toFixed(0) : a >= 10 ? v.toFixed(1) : v.toFixed(2)
  const unit = copy?.unit
  if (unit?.endsWith('%')) return `${num}%`
  if (unit?.startsWith('$')) return `$${num}`
  return num
}
// Percentile window ('1Y'/'3Y'/'4Y') — wire first, copy fallback.
const winTag = (m, copy) => (m.window ? m.window.toUpperCase() : (copy?.window ?? '—').replace(' range', '').replace('-year', 'Y').toUpperCase())
// Deterministic qualifier for when trend and level disagree (uses the
// direction-adjusted weaknessScore, so it's correct for weakensWhen both ways).
const levelQualifier = (m, improving) => {
  const w = m.weaknessScore
  if (w == null || improving == null) return ''
  if (improving && w >= 0.75) return ' · recovering from a weak stretch'
  if (!improving && w <= 0.25) return ' · off a strong stretch'
  return ''
}
// Cell state pill (shared by row + sheet)
const pillOf = (m) => {
  const discrete = m.severity !== null && m.severity !== undefined
  // STALE (fetch broken) / AGED (source hasn't published within its own lag
  // allowance) outrank TURN — a cell that isn't scoring must not read as one.
  return discrete ? [SEV[m.severity] ?? 'QUIET', SEV_TOK[m.severity] ?? SEV_TOK[0]]
    : m.tooYoung ? ['YOUNG', '--studio-text-subtle']
    : !m.fresh ? ['STALE', '--studio-text-subtle']
    : m.aged ? ['AGED', '--studio-text-subtle']
    : m.turning === true ? ['TURN', '--studio-danger']
    : ['CALM', '--studio-text-subtle']
}

/* ── one signal row: plain name + direction phrase / spark / arrow / pill ── */
function SignalRow({ m, first, onOpen }) {
  const copy = CELL_COPY[m.key]
  const discrete = m.severity !== null && m.severity !== undefined
  const improving = improvingOf(m)
  const phrase = discrete ? (m.severity > 0 ? 'recent events on the wire' : 'nothing on the wire')
    : improving == null || !copy?.dir ? null
    : (improving ? copy.dir.better : copy.dir.worse) + levelQualifier(m, improving)
  const phraseTok = discrete ? (m.severity > 0 ? SEV_TOK[m.severity] : '--studio-text-subtle')
    : improving == null ? '--studio-text-subtle' : improving ? '--studio-success' : '--studio-danger'
  const pill = pillOf(m)
  const span = spanTag(m)
  return (
    <button onClick={onOpen} style={{
      display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 68px 22px 56px', alignItems: 'center', gap: 8,
      width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer',
      padding: '10px 0', opacity: m.fresh || discrete ? 1 : 0.45,
      borderTop: first ? 'none' : '1px solid hsl(var(--studio-border) / 0.4)',
    }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ ...Sans, fontSize: 13, lineHeight: 1.3, color: 'hsl(var(--studio-text))', overflow: 'hidden',
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
          {copy?.plainName ?? m.label}
        </div>
        <div style={{ ...Mono, fontSize: 8.5, letterSpacing: '0.05em', marginTop: 2, color: `hsl(var(${phraseTok}))`, textTransform: 'uppercase' }}>
          {phrase ?? '—'}
        </div>
      </div>
      <div>
        {discrete ? null : hasSpark(m) ? (
          <>
            <Spark data={m.series} up={improving !== false} width={64} height={22} />
            {span && <div style={{ ...Mono, fontSize: 7, letterSpacing: '0.08em', color: 'hsl(var(--studio-text-subtle))', textAlign: 'center', marginTop: 1 }}>{span}</div>}
          </>
        ) : (
          <div style={{ ...Mono, fontSize: 7.5, letterSpacing: '0.08em', color: 'hsl(var(--studio-text-subtle))', textAlign: 'center' }}>COLLECTING<br />DATA</div>
        )}
      </div>
      <div style={{ ...Mono, fontSize: 10, textAlign: 'center', color: improving == null ? 'hsl(var(--studio-text-subtle))' : `hsl(var(${improving ? '--studio-success' : '--studio-danger'}))` }}>
        {discrete ? '·' : improving == null ? '·' : improving ? '▲' : '▼'}
      </div>
      <div style={{ textAlign: 'right' }}><Pill tok={pill[1]}>{pill[0]}</Pill></div>
    </button>
  )
}

/* ── WSTS regional breadth: context band in the sheet, never a vote.
   Derived from the wire (per-region slope direction vs the world number). ── */
const REGION_LABEL = { americas: 'AMERICAS', asia_pacific: 'ASIA-PAC', europe: 'EUROPE', japan: 'JAPAN' }
function RegionalBreadth({ regions }) {
  const known = regions.filter((r) => r.agrees != null)
  const agree = known.filter((r) => r.agrees).length
  return (
    <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid hsl(var(--studio-border) / 0.4)' }}>
      <div style={{ ...Mono, fontSize: 8, letterSpacing: '0.1em', color: 'hsl(var(--studio-text-subtle))', marginBottom: 8 }}>
        REGIONAL BREADTH
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        {regions.map((r) => (
          <div key={r.key} style={{ flex: 1, minWidth: 0 }}>
            <div style={{ ...Mono, fontSize: 7.5, letterSpacing: '0.08em', color: 'hsl(var(--studio-text-subtle))', marginBottom: 3 }}>
              {REGION_LABEL[r.key] ?? r.key.toUpperCase()}
            </div>
            <div style={{ ...Mono, fontSize: 12, color: r.up == null ? 'hsl(var(--studio-text-subtle))' : `hsl(var(${r.up ? '--studio-success' : '--studio-danger'}))` }}>
              {r.up == null ? '·' : r.up ? '▲' : '▼'}
            </div>
          </div>
        ))}
      </div>
      {known.length > 0 && (
        <div style={{ ...Mono, fontSize: 8, letterSpacing: '0.05em', color: 'hsl(var(--studio-text-subtle))', marginTop: 8 }}>
          {agree} OF {known.length} REGIONS POINT THE SAME WAY AS THE WORLD NUMBER
        </div>
      )}
    </div>
  )
}

/* ── the detail sheet: dense data + explainers (NewsSheet shell/motion) ── */
function CycleCellSheet({ m, nodeKey, onClose }) {
  const [shown, setShown] = useState(false)
  const [deepOpen, setDeepOpen] = useState(false)
  // Full transformed history (lazy; null → fall back to the 24-point Spark).
  const { data: fullSeries } = useCycleSeries(m && !(m.severity !== null && m.severity !== undefined) ? m.key : null)
  useEffect(() => { const a = requestAnimationFrame(() => setShown(true)); return () => cancelAnimationFrame(a) }, [])
  if (!m) return null
  const copy = CELL_COPY[m.key] ?? {}
  const node = NODE_COPY[nodeKey]
  const discrete = m.severity !== null && m.severity !== undefined
  const improving = improvingOf(m)
  const pill = pillOf(m)
  const phrase = discrete ? null : improving == null || !copy.dir ? null : improving ? copy.dir.better : copy.dir.worse

  const Fact = ({ k, v, tok }) => (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ ...Mono, fontSize: 8, letterSpacing: '0.1em', color: 'hsl(var(--studio-text-subtle))', marginBottom: 4 }}>{k}</div>
      <div style={{ ...Mono, fontSize: 13, color: tok ? `hsl(var(${tok}))` : 'hsl(var(--studio-text))', whiteSpace: 'nowrap' }}>{v}</div>
    </div>
  )
  const Para = ({ k, children }) => (
    <div style={{ marginTop: 16 }}>
      <div style={{ ...Mono, fontSize: 8.5, letterSpacing: '0.14em', color: 'hsl(var(--studio-accent))', marginBottom: 6 }}>{k}</div>
      <p style={{ ...Sans, fontSize: 13.5, lineHeight: 1.6, color: 'hsl(var(--studio-text-muted))', margin: 0 }}>{children}</p>
    </div>
  )

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 70 }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'hsl(var(--studio-overlay) / 0.66)', backdropFilter: 'blur(3px)', WebkitBackdropFilter: 'blur(3px)', opacity: shown ? 1 : 0, transition: 'opacity 220ms ease' }} />
      <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, maxHeight: '88%', display: 'flex', flexDirection: 'column',
        background: 'hsl(var(--studio-background))', borderTop: '2px solid hsl(var(--studio-accent) / 0.55)', borderRadius: '16px 16px 0 0',
        boxShadow: '0 -16px 44px hsl(var(--studio-overlay) / 0.6)',
        transform: shown ? 'translateY(0)' : 'translateY(100%)', transition: 'transform 340ms var(--studio-ease)', overflow: 'hidden' }}>
        <div style={{ padding: '9px 20px 0', flexShrink: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10 }}>
            <span onClick={onClose} style={{ width: 38, height: 4, borderRadius: 999, background: 'hsl(var(--studio-border))', cursor: 'pointer' }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ ...Mono, fontSize: 9.5, letterSpacing: '0.18em', color: 'hsl(var(--studio-accent))' }}>
              {(node?.name ?? nodeKey ?? '').toUpperCase()} · {m.label?.toUpperCase()}
            </span>
            <button onClick={onClose} aria-label="Close" style={{ width: 30, height: 30, borderRadius: 'var(--studio-radius-sm)', cursor: 'pointer', background: 'hsl(var(--studio-surface-2))', border: '1px solid hsl(var(--studio-border))', color: 'hsl(var(--studio-text-muted))', fontSize: 14 }}>✕</button>
          </div>
        </div>

        <div className="atlas-scroll" style={{ flex: 1, overflowY: 'auto', padding: '14px 20px 30px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ ...Display, fontSize: 22, lineHeight: 1.2, color: 'hsl(var(--studio-text))', textWrap: 'pretty' }}>
              {copy.plainName ?? m.label}
            </div>
            <div style={{ paddingTop: 6 }}><Pill tok={pill[1]}>{pill[0]}</Pill></div>
          </div>
          {phrase && (
            <div style={{ ...Mono, fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase', marginTop: 8,
              color: `hsl(var(${improving ? '--studio-success' : '--studio-danger'}))` }}>
              RIGHT NOW: {phrase}{levelQualifier(m, improving)} {improving ? '▲' : '▼'}
            </div>
          )}

          {!discrete && (fullSeries ? (
            <div style={{ marginTop: 12 }}>
              <CycleChart full={fullSeries} unit={copy.unit} width={330} height={190}
                up={improving !== false} />
            </div>
          ) : hasSpark(m) ? (
            <div style={{ marginTop: 14 }}>
              {chartCaption(m, copy) && (
                <div style={{ ...Mono, fontSize: 8, letterSpacing: '0.1em', color: 'hsl(var(--studio-text-subtle))', marginBottom: 6 }}>
                  {chartCaption(m, copy)}
                </div>
              )}
              <Spark data={m.series} up={improving !== false} width={330} height={54} />
            </div>
          ) : (
            <div style={{ ...Mono, fontSize: 9, letterSpacing: '0.1em', color: 'hsl(var(--studio-text-subtle))', marginTop: 16 }}>
              COLLECTING DATA — TOO LITTLE HISTORY TO DRAW YET
            </div>
          ))}

          {/* the dense read — what the old instrument panel showed */}
          <div style={{ display: 'flex', gap: 12, marginTop: 16, paddingTop: 14, borderTop: '1px solid hsl(var(--studio-border) / 0.4)' }}>
            {discrete ? (
              <>
                <Fact k="STATUS" v={SEV[m.severity] ?? 'QUIET'} tok={SEV_TOK[m.severity]} />
                <Fact k="FEED" v={copy.window ?? 'EVENTS'} />
                <Fact k="CADENCE" v={(copy.cadence ?? '—').toUpperCase()} />
              </>
            ) : (
              <>
                <Fact k="LATEST" v={fmtLatest(m, copy) ?? '—'} />
                <Fact k="LEVEL" v={m.tooYoung ? 'YOUNG' : fmtLevel(m.levelPct)} />
                <Fact k="VS" v={winTag(m, copy)} />
                <Fact k="UPDATED" v={monthsAgo(m.asOf) ?? '—'} tok={m.fresh && !m.aged ? null : '--studio-danger'} />
              </>
            )}
          </div>
          {discrete && m.silence && (
            <div style={{ ...Mono, fontSize: 8, letterSpacing: '0.05em', color: 'hsl(var(--studio-accent))', marginTop: 8 }}>
              A REGULAR BORROWER HAS GONE QUIET PAST ITS OWN ISSUANCE RHYTHM — READS WATCH
            </div>
          )}
          {!discrete && (
            <div style={{ ...Mono, fontSize: 8, letterSpacing: '0.05em', color: 'hsl(var(--studio-text-subtle))', marginTop: 8 }}>
              LEVEL = WHERE TODAY SITS IN THE LAST {winTag(m, copy)} OF DATA · P0 LOW, P100 HIGH
            </div>
          )}
          {!discrete && m.aged && (
            <div style={{ ...Mono, fontSize: 8, letterSpacing: '0.05em', color: 'hsl(var(--studio-danger) / 0.85)', marginTop: 6 }}>
              THE SOURCE HASN'T PUBLISHED WITHIN ITS NORMAL LAG — SHOWN FOR CONTEXT, NOT SCORED
            </div>
          )}
          {Array.isArray(m.regions) && m.regions.length > 0 && <RegionalBreadth regions={m.regions} />}

          {copy.what && <Para k="WHAT THIS MEANS">{copy.what}</Para>}
          {copy.why && <Para k="WHY WE TRACK IT">{copy.why}</Para>}
          {copy.pair && (
            <Para k={`READ IT WITH · ${(CELL_COPY[copy.pair.key]?.plainName ?? copy.pair.key).toUpperCase()}`}>
              {copy.pair.note}
            </Para>
          )}

          {/* opt-in depth: the precise term is in the title; the education
              lives here, collapsed until asked for */}
          {copy.deep && (
            <div style={{ marginTop: 16 }}>
              <button onClick={() => setDeepOpen((v) => !v)} style={{
                display: 'flex', alignItems: 'center', gap: 7, width: '100%', textAlign: 'left',
                background: 'none', border: 'none', cursor: 'pointer', padding: 0,
              }}>
                <span aria-hidden style={{
                  ...Mono, fontSize: 10, width: 15, height: 15, borderRadius: '50%', flexShrink: 0,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  color: 'hsl(var(--studio-accent))', border: '1px solid hsl(var(--studio-accent) / 0.45)',
                }}>i</span>
                <span style={{ ...Mono, fontSize: 8.5, letterSpacing: '0.14em', color: 'hsl(var(--studio-accent))' }}>
                  THE FULL STORY
                </span>
                <span style={{ ...Mono, fontSize: 9, color: 'hsl(var(--studio-text-subtle))' }}>{deepOpen ? '▾' : '▸'}</span>
              </button>
              {deepOpen && copy.deep.map((para, i) => (
                <p key={i} style={{ ...Sans, fontSize: 13.5, lineHeight: 1.6, color: 'hsl(var(--studio-text-muted))', margin: '10px 0 0' }}>{para}</p>
              ))}
            </div>
          )}

          {copy.source && (
            <div style={{ ...Mono, fontSize: 9, letterSpacing: '0.05em', color: 'hsl(var(--studio-text-subtle))', marginTop: 18, paddingTop: 12, borderTop: '1px solid hsl(var(--studio-border) / 0.4)' }}>
              SOURCE · {copy.source.toUpperCase()}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

/* ── loading skeleton ── */
function PanelSkeleton() {
  return (
    <div style={{ padding: '10px 0' }}>
      <Skel w="100%" h={150} r={8} />
      <div style={{ marginTop: 16 }}>
        {Array.from({ length: 7 }).map((_, i) => (
          <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '12px 0', borderTop: i ? '1px solid hsl(var(--studio-border) / 0.3)' : 'none' }}>
            <div style={{ flex: 1 }}><Skel w="60%" h={12} /></div>
            <Skel w={64} h={18} /><Skel w={44} h={13} />
          </div>
        ))}
      </div>
    </div>
  )
}

/* ── first-run "what this is" explainer — same sheet shell/motion as the cell
   sheet. Auto-shows ONCE for new visitors (useUserPref-gated below), stays
   reachable anytime from the header "?", and carries a don't-show-again box.
   All close paths respect the box: checked → persist so it never auto-shows
   again; unchecked → session-only, greets again next visit. ── */
function CycleIntroSheet({ onClose }) {
  const [shown, setShown] = useState(false)
  const [dontShow, setDontShow] = useState(true)
  useEffect(() => { const a = requestAnimationFrame(() => setShown(true)); return () => cancelAnimationFrame(a) }, [])
  const close = () => onClose(dontShow)

  const Point = ({ k, children }) => (
    <div style={{ marginTop: 16 }}>
      <div style={{ ...Mono, fontSize: 8.5, letterSpacing: '0.14em', color: 'hsl(var(--studio-accent))', marginBottom: 5 }}>{k}</div>
      <p style={{ ...Sans, fontSize: 13.5, lineHeight: 1.6, color: 'hsl(var(--studio-text-muted))', margin: 0 }}>{children}</p>
    </div>
  )

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 72 }}>
      <div onClick={close} style={{ position: 'absolute', inset: 0, background: 'hsl(var(--studio-overlay) / 0.66)', backdropFilter: 'blur(3px)', WebkitBackdropFilter: 'blur(3px)', opacity: shown ? 1 : 0, transition: 'opacity 220ms ease' }} />
      <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, maxHeight: '90%', display: 'flex', flexDirection: 'column',
        background: 'hsl(var(--studio-background))', borderTop: '2px solid hsl(var(--studio-accent) / 0.55)', borderRadius: '16px 16px 0 0',
        boxShadow: '0 -16px 44px hsl(var(--studio-overlay) / 0.6)',
        transform: shown ? 'translateY(0)' : 'translateY(100%)', transition: 'transform 340ms var(--studio-ease)', overflow: 'hidden' }}>
        <div style={{ padding: '9px 20px 0', flexShrink: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10 }}>
            <span onClick={close} style={{ width: 38, height: 4, borderRadius: 999, background: 'hsl(var(--studio-border))', cursor: 'pointer' }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ ...Mono, fontSize: 9.5, letterSpacing: '0.18em', color: 'hsl(var(--studio-accent))' }}>WHAT THIS IS</span>
            <button onClick={close} aria-label="Close" style={{ width: 30, height: 30, borderRadius: 'var(--studio-radius-sm)', cursor: 'pointer', background: 'hsl(var(--studio-surface-2))', border: '1px solid hsl(var(--studio-border))', color: 'hsl(var(--studio-text-muted))', fontSize: 14 }}>✕</button>
          </div>
        </div>

        <div className="atlas-scroll" style={{ flex: 1, overflowY: 'auto', padding: '14px 20px 22px' }}>
          <div style={{ ...Display, fontSize: 24, lineHeight: 1.15, color: 'hsl(var(--studio-text))', textWrap: 'pretty' }}>
            The AI build-out, read from the supply chain
          </div>
          <p style={{ ...Sans, fontSize: 13.5, lineHeight: 1.6, color: 'hsl(var(--studio-text-muted))', margin: '10px 0 0' }}>
            This page watches the real machinery behind the AI boom — the borrowing, the chip-making gear, the electricity, the exports — and reads whether the cycle is still building or starting to cool.
          </p>

          <Point k="WHAT IT TRACKS">
            Hard data, not opinions: credit spreads, chip-equipment orders, daily AI usage, data-center construction, and chip exports from the countries that ship them.
          </Point>
          <Point k="LEADING VS CONFIRMING">
            Some signals tend to move first — credit, chip equipment, AI usage. Those are marked LEADING and drive the headline. The rest confirm the story later.
          </Point>
          <Point k="THE HEADLINE">
            One word up top — STABLE, DIVERGENT, EARLY WARNING — is the panel's overall read. It describes what's moving, never what to buy or sell.
          </Point>
          <Point k="EACH SIGNAL">
            Every row is a plain-English signal with a direction and a state tag (CALM, TURN, WATCH). Tap any row for the full story, its chart, and where the number comes from.
          </Point>
          <Point k="THE CATCH">
            It's a young instrument — a directional read, not a verdict, and it can't tell you why something is moving. A divergence is a shape worth watching, not a call.
          </Point>

          <label style={{ display: 'flex', alignItems: 'center', gap: 9, marginTop: 22, cursor: 'pointer' }}>
            <input type="checkbox" checked={dontShow} onChange={(e) => setDontShow(e.target.checked)}
              style={{ width: 16, height: 16, accentColor: 'hsl(var(--studio-accent))', cursor: 'pointer' }} />
            <span style={{ ...Sans, fontSize: 12.5, color: 'hsl(var(--studio-text-muted))' }}>Don&apos;t show this again</span>
          </label>

          <button onClick={close} style={{ ...Mono, fontSize: 11, letterSpacing: '0.1em', marginTop: 16, width: '100%', padding: '12px 0',
            borderRadius: 'var(--studio-radius-sm)', cursor: 'pointer', background: 'hsl(var(--studio-accent) / 0.14)',
            border: '1px solid hsl(var(--studio-accent) / 0.45)', color: 'hsl(var(--studio-accent))' }}>
            GOT IT
          </button>
        </div>
      </div>
    </div>
  )
}

/* Shared cycle content — header band + signals + detail sheet, WITHOUT the
   mobile shell (TickerStrip/BottomNav). Reads openTicker from TickerCtx via
   useTicker(), so it renders under both the mobile StockDetailProvider and the
   desktop TickerPanelProvider. `desktop`: natural document flow (the desktop
   shell owns the page scroll) vs the mobile internal flex scroll. */
/* The Read — collapsed to a two-line teaser by default.
   It's a full paragraph of weekly commentary, and at full height it pushed the
   signal rows and their charts below the fold on both phone and desktop. The
   charts are the reason to be on this page, so the words yield to them until
   asked for. The toggle only renders when the text actually overflows two
   lines (measured, not assumed) — a short Read shouldn't grow a dead button. */
function TheRead({ body, weekOf }) {
  const [open, setOpen] = useState(false)
  const [clamped, setClamped] = useState(false)
  const pRef = useRef(null)

  // Measure against the CLAMPED box, so this only runs meaningfully while
  // collapsed; once open the button is kept by `open` itself.
  useEffect(() => {
    const el = pRef.current
    if (el && !open) setClamped(el.scrollHeight > el.clientHeight + 1)
  }, [body, open])

  return (
    <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid hsl(var(--studio-border) / 0.4)' }}>
      <Kicker>THE READ{weekOf ? ` · WEEK OF ${weekOf}` : ''}</Kicker>
      <p
        ref={pRef}
        style={{
          ...Sans, fontSize: 13.5, lineHeight: 1.65, color: 'hsl(var(--studio-text-muted))', margin: '8px 0 0',
          ...(open ? null : { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }),
        }}
      >
        {body}
      </p>
      {(clamped || open) && (
        <button
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          style={{
            all: 'unset', cursor: 'pointer', display: 'inline-block', marginTop: 7,
            ...Mono, fontSize: 9, letterSpacing: '0.12em', color: 'hsl(var(--studio-accent))',
          }}
        >
          {open ? 'LESS \u2191' : 'READ MORE \u2193'}
        </button>
      )}
    </div>
  )
}

export function CycleContent({ desktop = false }) {
  const { assessment, nodes, hero, read, staleness, loading } = useCyclePanel()
  const [open, setOpen] = useState(null) // { m, nodeKey }
  // First-run explainer: dismissed-flag persists per user (server) / device
  // (localStorage). Auto-show once when hydrated & not dismissed; the header
  // "?" reopens it anytime.
  const [introDismissed, setIntroDismissed, introHydrated] = useUserPref('cycle_intro_dismissed', false)
  const [introOpen, setIntroOpen] = useState(false)
  const introAutoShown = useRef(false)
  useEffect(() => {
    if (introHydrated && !introDismissed && !introAutoShown.current) {
      introAutoShown.current = true
      setIntroOpen(true)
    }
  }, [introHydrated, introDismissed])
  const closeIntro = (dontShow) => { if (dontShow) setIntroDismissed(true); setIntroOpen(false) }
  const meta = STATE_META[assessment?.state] ?? STATE_META['insufficient-data']
  // Ordered demand groups; any server-side group NODE_ORDER doesn't know yet
  // still renders (appended) instead of silently vanishing.
  const demandNodes = [
    ...NODE_ORDER.map((k) => [k, nodes[k]]).filter(([, n]) => n),
    ...Object.entries(nodes).filter(([k, n]) => n.panel === 'demand' && !NODE_ORDER.includes(k)),
  ]
  const supply = Object.entries(nodes).find(([, n]) => n.panel === 'supply')

  // Geographies whose signals are turning → red pins on the map.
  const hotGeos = new Set()
  for (const [, n] of demandNodes) for (const m of n.members ?? []) {
    if (m.turning === true) for (const g of CELL_COPY[m.key]?.geo ?? []) hotGeos.add(g)
  }
  // The policy tripwire is the map's most geographic signal: a live BREAK
  // (severity 2) lights its corridor pins red. WATCH stays off the map —
  // the page pill carries it, and the tripwire "says look now, not how bad".
  if ((supply?.[1]?.members?.[0]?.severity ?? 0) >= 2) {
    for (const g of CELL_COPY.supply_events?.geo ?? []) hotGeos.add(g)
  }

  const weekOf = read?.week_of ? fmtWeekOf(read.week_of) : null

  return (
    <>
        {/* header band */}
        <div style={{ flexShrink: 0, padding: desktop ? '0 0 4px' : '20px 20px 0' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
            <div style={{ ...Display, fontSize: 33, lineHeight: 1.05, color: 'hsl(var(--studio-text))' }}>The Cycle</div>
            {!loading && assessment && (
              <span style={{ ...Mono, fontSize: 11, letterSpacing: '0.1em', color: `hsl(var(${meta.tok}))`, borderBottom: `1px solid hsl(var(${meta.tok}) / 0.55)`, paddingBottom: 1 }}>
                {meta.label}
              </span>
            )}
            <button onClick={() => setIntroOpen(true)} aria-label="What is this?"
              style={{ ...Mono, fontSize: 11, marginLeft: 'auto', alignSelf: 'center', width: 22, height: 22, flexShrink: 0, borderRadius: '50%',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                background: 'none', border: '1px solid hsl(var(--studio-accent) / 0.45)', color: 'hsl(var(--studio-accent))' }}>?</button>
          </div>
          <div style={{ ...Mono, fontSize: 9, letterSpacing: '0.06em', color: 'hsl(var(--studio-text-subtle))', marginTop: 5, marginBottom: 10 }}>
            {loading ? 'READING THE PANEL…' : assessment ? `AS OF ${assessment.asOf ?? '—'}` : 'PANEL UNAVAILABLE'}
            {!loading && assessment && staleness.length > 0 && (
              <span style={{ color: 'hsl(var(--studio-danger))' }}>
                {` · ${staleness.length} SIGNAL${staleness.length > 1 ? 'S' : ''} STALE`}
              </span>
            )}
          </div>
        </div>

        {/* scroll body */}
        <div className="atlas-scroll" style={desktop ? { padding: '0 0 24px' } : { flex: 1, minHeight: 0, overflowY: 'auto', padding: '0 20px 24px' }}>
          {loading ? <PanelSkeleton /> : !assessment ? (
            <div style={{ ...Mono, fontSize: 10, letterSpacing: '0.08em', color: 'hsl(var(--studio-text-subtle))', textAlign: 'center', padding: '48px 0' }}>
              PANEL UNAVAILABLE — TRY AGAIN SHORTLY
            </div>
          ) : (
            <>
              {/* ── altitude 1: the world + the sentence + the Read ── */}
              {/* Desktop: the map keeps full page width; the reading column
                  below it (sentence, Read, signals) caps so rows don't stretch
                  to 1320px. Mobile: no cap (undefined). */}
              <CycleMap hotGeos={hotGeos} />
              <div className={desktop ? 'v3web-list' : undefined}>
              {hero && (
                <div style={{ ...Display, fontSize: 17.5, lineHeight: 1.42, color: 'hsl(var(--studio-text))', marginTop: 6, textWrap: 'pretty' }}>
                  {hero}
                </div>
              )}
              {read?.body && <TheRead body={read.body} weekOf={weekOf} />}


              {/* ── altitude 2: the signals, in plain English ── */}
              <div style={{ marginTop: 24 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                  <Kicker>THE SIGNALS</Kicker>
                </div>
                {demandNodes.map(([nodeKey, node]) => (
                  <div key={nodeKey}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '16px 0 0' }}>
                      <span style={{ ...Mono, fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: node.votesDiffusion ? 'hsl(var(--studio-accent))' : 'hsl(var(--studio-text-muted))' }}>
                        {NODE_COPY[nodeKey]?.name ?? node.label}{node.votesDiffusion ? ' · LEADING' : ''}
                      </span>
                      <span style={{ flex: 1, height: 1, background: 'hsl(var(--studio-border) / 0.5)' }} />
                      <span style={{ ...Mono, fontSize: 8.5, letterSpacing: '0.08em', color: `hsl(var(${node.turning === true ? '--studio-danger' : '--studio-text-subtle'}))` }}>
                        {node.turning === true ? 'TURNING' : node.turning === false ? 'CALM' : 'NO READ'}
                      </span>
                    </div>
                    {node.members.map((m, i) => (
                      <SignalRow key={m.key} m={m} first={i === 0} onOpen={() => setOpen({ m, nodeKey })} />
                    ))}
                  </div>
                ))}
              </div>

              {/* ── supply tripwire ── */}
              <div style={{ marginTop: 22 }}>
                <Kicker>THE POLICY WILDCARD</Kicker>
                <button onClick={() => supply?.[1]?.members?.[0] && setOpen({ m: supply[1].members[0], nodeKey: supply[0] })}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 0', width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ ...Sans, fontSize: 13, color: 'hsl(var(--studio-text))' }}>Export controls &amp; critical minerals</div>
                  </div>
                  <Pill tok={SEV_TOK[supply?.[1]?.members?.[0]?.severity ?? 0]}>{SEV[supply?.[1]?.members?.[0]?.severity ?? 0]}</Pill>
                </button>
              </div>

              {/* honesty footnote */}
              <div style={{ ...Mono, fontSize: 8.5, letterSpacing: '0.05em', color: 'hsl(var(--studio-text-subtle))', marginTop: 20, paddingTop: 12, borderTop: '1px solid hsl(var(--studio-border) / 0.4)' }}>
                DEMAND THRESHOLDS CALIBRATED AGAINST 9 CHIP DOWNTURNS SINCE 1989 · CREDIT NOT YET VALIDATED · DIRECTIONAL READ, NOT LEVELS
              </div>
              </div>
            </>
          )}
        </div>

        {open && <CycleCellSheet m={open.m} nodeKey={open.nodeKey} onClose={() => setOpen(null)} />}
        {introOpen && <CycleIntroSheet onClose={closeIntro} />}
    </>
  )
}

/* mobile wrapper — provides the ticker drawer context + the mobile shell
   (TickerStrip tape + BottomNav) around the shared CycleContent. */
function CycleBody() {
  const { openTicker } = useStockDetail()
  return (
    <TickerCtx.Provider value={openTicker}>
      <div style={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column',
        background: 'radial-gradient(140% 75% at 50% -10%, hsl(var(--studio-background)) 0%, hsl(var(--studio-overlay)) 100%)' }}>
        {/* Slim pill row rather than reaching into CycleContent — that component
            is shared with v3desktop, which has its own ⌘K palette and must not
            grow a second search affordance. */}
        <div style={{ flexShrink: 0, padding: '14px 20px 0', display: 'flex', justifyContent: 'flex-end' }}>
          <SearchPill />
        </div>
        <CycleContent />
        <div style={{ flexShrink: 0, borderTop: '1px solid hsl(var(--studio-border) / 0.5)', background: 'hsl(var(--studio-background) / 0.72)', backdropFilter: 'blur(12px)' }}>
          <TickerStrip />
        </div>
        <BottomNav />
      </div>
    </TickerCtx.Provider>
  )
}

export function Cycle() {
  return (
    <div className="v3-root" style={{ height: 'var(--v3-vh, 100dvh)', display: 'flex', flexDirection: 'column' }}>
      <StockDetailProvider>
        <CycleBody />
      </StockDetailProvider>
    </div>
  )
}
